import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Sparkles, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { getVenueDirectory } from "@/lib/api";

/**
 * FoundingVenueWall — social-proof grid of the first Founding Venue partners in DFW.
 * Renders only when at least one founding venue exists; hidden completely otherwise
 * (no empty states on the homepage). Never fabricates venues.
 */

const initials = (name) =>
  (name || "?")
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const VenueCard = ({ v }) => {
  const badge = (v.badges || []).includes("founding_venue");
  return (
    <Link
      to={`/venues/${v.slug}`}
      data-testid={`founding-venue-${v.slug}`}
      className="group relative h-full glass rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform duration-300"
    >
      <div className="aspect-[4/3] relative bg-gradient-to-br from-neon-cyan/20 to-neon-violet/20 grid place-items-center overflow-hidden">
        {v.photo_url ? (
          <img
            src={v.photo_url}
            alt={`${v.name} venue photo`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <span className="font-display text-5xl font-black text-white/70 tracking-tight">
            {initials(v.name)}
          </span>
        )}
        {v.live_now > 0 && (
          <span className="absolute top-3 right-3 flex items-center gap-1.5 rounded-full bg-black/70 backdrop-blur-sm px-2.5 py-1 text-[11px] font-semibold text-neon-cyan ring-1 ring-neon-cyan/40">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-neon-cyan opacity-70 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-neon-cyan" />
            </span>
            {v.live_now} live
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold text-white truncate">{v.name}</p>
          {badge && (
            <span className="shrink-0 rounded-md bg-neon-cyan/15 text-neon-cyan text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 flex items-center gap-1">
              <Sparkles className="w-2.5 h-2.5" /> Founding
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-zinc-500 flex items-center gap-1 truncate">
          {v.neighborhood ? (
            <>
              <MapPin className="w-3 h-3" />
              {v.neighborhood}
            </>
          ) : (
            <span className="text-zinc-600">DFW</span>
          )}
        </p>
      </div>
    </Link>
  );
};

export default function FoundingVenueWall() {
  const [venues, setVenues] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancel = false;
    getVenueDirectory()
      .then((list) => {
        if (cancel) return;
        const founding = (list || [])
          .filter((v) => (v.badges || []).includes("founding_venue"))
          .sort((a, b) => (b.live_now || 0) - (a.live_now || 0))
          .slice(0, 8);
        setVenues(founding);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  // Hidden completely until founding venues exist — no empty states on the homepage.
  if (!loaded || venues.length === 0) return null;

  return (
    <section
      id="founding-venues"
      className="relative py-24 md:py-28 overflow-hidden"
      data-testid="founding-venue-wall"
    >
      <div className="glow-orb w-[420px] h-[420px] bg-neon-cyan/10 top-10 -right-40" />
      <div className="glow-orb w-[420px] h-[420px] bg-neon-violet/10 bottom-0 -left-40" />

      <div className="container-x relative z-10">
        <Reveal className="max-w-2xl">
          <p className="overline mb-4">Founding partner rooms</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight leading-[0.95]">
            Founding Venues, live in <span className="text-gradient">DFW</span>.
          </h2>
          <p className="mt-5 text-lg text-zinc-400 leading-relaxed max-w-xl">
            These are the first spots turning their nights into live rooms. Tap in when you get there.
          </p>
        </Reveal>

        <div className="mt-12">
          <div
            className={
              venues.length === 1
                ? "grid grid-cols-1 max-w-sm mx-auto"
                : venues.length === 2
                ? "grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto"
                : venues.length === 3
                ? "grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto"
                : "grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
            }
          >
            {venues.map((v) => (
              <VenueCard key={v.id || v.slug} v={v} />
            ))}
          </div>
          {venues.length >= 4 ? (
            <div className="mt-10 text-center">
              <Link
                to="/venues/directory"
                data-testid="founding-venue-see-all"
                className="inline-flex items-center gap-2 rounded-full glass hover:glass-cyan px-6 py-3 text-sm font-medium text-white transition-all"
              >
                See every venue on GateMate
                <ArrowRight className="w-4 h-4 text-neon-cyan" />
              </Link>
            </div>
          ) : (
            <p className="mt-10 text-center text-sm text-zinc-500">
              More Founding Venues on the way — 25 slots open in DFW.{" "}
              <Link
                to="/for-venues"
                data-testid="founding-venue-recruit-inline"
                className="text-neon-cyan hover:text-white transition-colors font-medium"
              >
                Claim one →
              </Link>
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
